import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  FilterChip,
  type SelectOption,
} from "@h2j-data-team/ui";

import { cn } from "@/lib/utils";
import { pickerMessages } from "@/lib/ui-labels";

/**
 * A toolbar filter over a list of values: a chip ("Space: Dev, +2") that opens a
 * searchable checklist. Several of them fit in a table toolbar, where a
 * MultiSelect per filter would wrap the row once values are picked.
 */
/** Accent- and case-insensitive. */
const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * cmdk filter that matches the option label only (passed as `keywords`): the
 * item value is an opaque id (an owner id) or a raw enum, which a fuzzy match
 * would hit on random letters.
 */
function matchLabel(_value: string, search: string, keywords?: string[]) {
  return normalize(keywords?.join(" ") ?? "").includes(normalize(search.trim())) ? 1 : 0;
}

export function FacetFilter({
  label,
  options,
  value,
  onValueChange,
}: {
  /** The noun the chip names ("Space"). */
  label: string;
  options: readonly SelectOption[];
  value: readonly string[];
  onValueChange: (value: string[]) => void;
}) {
  const { t } = useTranslation("common");
  const messages = pickerMessages();
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  const toggle = (v: string) =>
    onValueChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <FilterChip
      label={label}
      summary={
        value.length === 0
          ? undefined
          : `${labelOf(value[0])}${value.length > 1 ? `, +${value.length - 1}` : ""}`
      }
      clearLabel={t("clearSelection")}
      onClear={() => onValueChange([])}
    >
      <Command label={label} filter={matchLabel}>
        <CommandInput placeholder={messages.searchPlaceholder} />
        <CommandList label={label}>
          <CommandEmpty>{messages.emptyLabel}</CommandEmpty>
          {options.map((option) => {
            const checked = value.includes(option.value);
            return (
              <CommandItem
                key={option.value}
                value={option.value}
                keywords={[option.label]}
                onSelect={() => toggle(option.value)}
              >
                <Check className={cn("h-4 w-4 shrink-0", !checked && "invisible")} aria-hidden />
                <span className="truncate">{option.label}</span>
                {checked && <span className="sr-only">{messages.selectedLabel}</span>}
              </CommandItem>
            );
          })}
        </CommandList>
      </Command>
    </FilterChip>
  );
}
